import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Check, Dumbbell, Trophy } from 'lucide-react';
import { Button } from '../ui/button';
import { useBackHandler } from '../../lib/backNav';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateLong = (s) => new Date(s + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
const fmtDur = (sec) => {
  if (sec == null) return '—';
  sec = Math.max(0, Math.floor(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const CalendarView = ({ api, token, onStartWorkout }) => {
  const today = new Date();
  const todayStr = ymd(today);
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [data, setData] = useState({ scheduled: [], completed: [] });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const start = ymd(new Date(cursor.y, cursor.m, 1));
    const end = ymd(new Date(cursor.y, cursor.m + 2, 0)); // covers cursor month + next
    api.get(`/calendar?start=${start}&end=${end}`, token)
      .then(setData)
      .catch(() => setData({ scheduled: [], completed: [] }));
  }, [api, token, cursor]);

  const schedByDate = {};
  for (const s of data.scheduled) schedByDate[s.date] = s;
  const compByDate = {};
  for (const c of data.completed) compByDate[c.date] = c;

  // The single next upcoming (future, not-yet-done) scheduled day — gets a
  // head-start Start button. Only trustworthy when today is in the loaded window
  // (otherwise we'd flag an arbitrary far-future day, allowing over-jumping).
  const winStart = ymd(new Date(cursor.y, cursor.m, 1));
  const winEnd = ymd(new Date(cursor.y, cursor.m + 2, 0));
  const todayInWindow = todayStr >= winStart && todayStr <= winEnd;
  const nextUpcoming = todayInWindow
    ? (data.scheduled.filter((s) => s.date > todayStr && !compByDate[s.date]).map((s) => s.date).sort()[0] || null)
    : null;

  const move = (delta) => setCursor((c) => {
    const nm = c.m + delta;
    return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });

  const second = { y: cursor.y + Math.floor((cursor.m + 1) / 12), m: (cursor.m + 1) % 12 };
  const gridProps = { schedByDate, compByDate, todayStr, onSelect: setSelected };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-2xl">Calendar</h2>
        <div className="flex gap-1">
          <button onClick={() => move(-1)} className="h-9 w-9 rounded-md border flex items-center justify-center hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })} className="px-3 h-9 rounded-md border text-sm hover:bg-accent">Today</button>
          <button onClick={() => move(1)} className="h-9 w-9 rounded-md border flex items-center justify-center hover:bg-accent"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>

      <MonthGrid y={cursor.y} m={cursor.m} {...gridProps} />
      <MonthGrid y={second.y} m={second.m} {...gridProps} />

      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-primary/15 border border-primary/40 inline-block" /> completed</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-card border border-input inline-block" /> scheduled</span>
      </div>

      {selected && (
        <DayDetail
          api={api} token={token} selected={selected} todayStr={todayStr} nextUpcoming={nextUpcoming}
          onStartWorkout={onStartWorkout} onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

const MonthGrid = ({ y, m, schedByDate, compByDate, todayStr, onSelect }) => {
  const first = new Date(y, m, 1);
  const lead = first.getDay(); // Sunday = 0
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="mb-6">
      <h3 className="font-semibold mb-2">{MONTHS[m]} {y}</h3>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW.map((d) => <div key={d} className="text-center text-xs text-muted-foreground font-medium">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const date = ymd(new Date(y, m, d));
          const sched = schedByDate[date];
          const comp = compByDate[date];
          const focus = sched?.focus;
          const isToday = date === todayStr;
          const clickable = sched || comp;
          return (
            <button
              key={i}
              disabled={!clickable}
              onClick={() => onSelect({ date, sched, comp })}
              className={`aspect-square rounded-md border p-1 flex flex-col items-stretch text-left disabled:opacity-100 ${
                comp ? 'bg-primary/15 border-primary/40' : sched ? 'bg-card border-input' : 'border-transparent'
              } ${isToday ? 'ring-2 ring-primary' : ''} ${clickable ? 'hover:border-primary' : ''}`}
            >
              <div className="flex items-start justify-between leading-none">
                <span className={`text-xs font-medium ${comp ? 'text-foreground' : 'text-muted-foreground'}`}>{d}</span>
                {comp && <Check className="h-3.5 w-3.5 text-primary flex-none" />}
              </div>
              {focus && (
                <span className="text-[9px] leading-tight text-primary mt-auto truncate w-full text-center">{focus}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DayDetail = ({ api, token, selected, todayStr, nextUpcoming, onStartWorkout, onClose }) => {
  useBackHandler(true, onClose);
  const { date, sched, comp } = selected;
  // Startable = a scheduled, not-yet-done day that is today, a missed past day, or the next upcoming one.
  const startable = !comp && !!sched && (date === todayStr || date < todayStr || date === nextUpcoming);
  const startLabel = date === todayStr ? 'Start this workout' : date < todayStr ? 'Make up this workout' : 'Start this early';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [assessLoading, setAssessLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p = comp
      ? api.get(`/sessions/${comp.session_id}`, token)
      : sched
        ? api.get(`/workout?date=${date}`, token)
        : Promise.resolve(null);
    p.then((d) => { setDetail(d); setAssessment(d?.ai_assessment || null); })
      .catch(() => setDetail(null)).finally(() => setLoading(false));
  }, [api, token, comp, sched]);

  const genAssessment = () => {
    if (!comp) return;
    setAssessLoading(true);
    api.post(`/sessions/${comp.session_id}/assessment`, {}, token)
      .then((r) => setAssessment(r.assessment))
      .catch(() => {})
      .finally(() => setAssessLoading(false));
  };

  // Group a completed session's sets by exercise, then merge with the full
  // prescribed list so every exercise shows (incl. cardio with no logged sets).
  const grouped = {};
  if (comp && detail?.sets) {
    for (const s of detail.sets) {
      const k = s.exercise_name || `#${s.exercise_id}`;
      (grouped[k] ||= []).push(s);
    }
  }
  const program = (comp && detail?.program) || [];
  const items = [
    ...program.map((p) => ({ name: p.name, target: `${p.target_sets} × ${p.reps_text}`, sets: grouped[p.name] || [] })),
    ...Object.keys(grouped).filter((n) => !program.some((p) => p.name === n)).map((n) => ({ name: n, target: null, sets: grouped[n] })),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border-t sm:border rounded-t-xl sm:rounded-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-card border-b px-4 py-3 flex items-center justify-between flex-none rounded-t-xl">
          <div>
            <div className="font-semibold">{fmtDateLong(date)}</div>
            {sched && <div className="text-xs text-primary">Week {sched.week} · {DOW[sched.day]} · {sched.focus}</div>}
          </div>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>

        <div className="p-4 overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
          ) : comp ? (
            <>
              <div className="flex items-center gap-2 mb-3 text-primary"><Trophy className="h-5 w-5" /><span className="font-medium">Completed</span></div>
              <div className="grid grid-cols-2 gap-2 mb-4 text-center">
                <Stat label="Time" value={fmtDur(comp.duration)} />
                <Stat label="Volume" value={`${Math.round(Number(comp.volume)).toLocaleString()}`} sub="lb" />
                <Stat label="Sets" value={comp.sets} />
                <Stat label="Exercises" value={comp.exercises} />
              </div>

              {assessment || assessLoading ? (
                <div className="rounded-lg bg-muted/50 border p-3 mb-4">
                  <div className="flex items-center gap-1.5 mb-1.5 text-primary"><img src="/icon-192.png" alt="" className="h-5 w-5 rounded" /><span className="text-sm font-semibold">Coach's feedback</span></div>
                  {assessLoading ? <p className="text-sm text-muted-foreground">Analyzing…</p> : <p className="text-sm leading-relaxed">{assessment}</p>}
                </div>
              ) : (
                <button onClick={genAssessment} className="w-full mb-4 flex items-center justify-center gap-1.5 h-10 rounded-md border text-sm text-primary hover:bg-accent">
                  <img src="/icon-192.png" alt="" className="h-4 w-4 rounded" /> Get coach feedback
                </button>
              )}

              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.name} className="rounded-lg border bg-background p-2.5">
                    <div className="text-sm font-medium mb-1.5">{it.name}</div>
                    {it.sets.length > 0 ? (
                      <div className="space-y-1">
                        {it.sets.map((s, j) => (
                          <div key={j} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Set {s.set_number || j + 1}</span>
                            <span className="font-medium">{Number(s.weight)}×{s.reps}{s.rpe ? ` @${s.rpe}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">{it.target ? `${it.target} · ` : ''}not logged</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : detail ? (
            <>
              <div className="space-y-2">
                {(detail.blocks || []).map((b, bi) => (
                  <div key={bi} className={`rounded-lg border bg-background p-2.5 ${b.type !== 'single' ? 'border-primary/40' : ''}`}>
                    {b.type !== 'single' && (
                      <div className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">
                        {b.type === 'superset' ? 'Superset' : 'Circuit'}{b.rounds ? ` · ${b.rounds} rounds` : ''}
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {b.exercises.map((ex, ei) => (
                        <div key={ei} className="flex items-start gap-2">
                          {b.type !== 'single' && <span className="text-primary/60 text-sm mt-0.5">•</span>}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{ex.name}</div>
                            <div className="text-xs text-muted-foreground">{ex.target_sets} × {ex.reps_text}{ex.suggested_text ? ` · ${ex.suggested_text}` : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {startable && (
                <Button className="w-full h-12 mt-4" onClick={() => { onClose(); onStartWorkout(date); }}>{startLabel}</Button>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6"><Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-40" />Nothing scheduled.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="rounded-lg border bg-background p-2">
    <div className="text-lg font-bold">{value}<span className="text-xs font-normal text-muted-foreground">{sub ? ` ${sub}` : ''}</span></div>
    <div className="text-[11px] text-muted-foreground">{label}</div>
  </div>
);

export default CalendarView;
